import { formatLocalizedCurrency } from './i18n.mjs?v=20260806.100';
import { containerStatusLabel } from './container-status-presentation.mjs?v=20260806.100';
import { oceanStatusCounts } from './ocean-status-order.mjs?v=20260806.100';

/**
 * Company Overview - one card per customer, replacing the Status page
 * (Alan, 2026-08-08). Everyone internal sees it; a customer does not, and a
 * driver has no web console at all.
 *
 * Completed means the empty is back, which is Alan's own definition: the job is
 * not finished when the box is delivered, it is finished when the equipment is
 * returned. Everything else on the card is the arithmetic around that.
 *
 * Measured against production on 2026-08-08 before any of this was written
 * (scripts/survey_company_overview_fields.js), because a card full of confident
 * zeros is worse than no card:
 *
 *   452 live containers, 9 customers, every one attributed by customer_name
 *   0    at empty_returned - nobody has completed anything yet
 *   80   carry customer_quote_display; 372 hold a real null. $46,550 in total
 *   0    carry any of the seven charge fields, and customer_paid is absent on all
 *   36   are past their LFD, 31 of those also carry status past_due
 *
 * So Revenue is the agreed quote plus whatever accessorials Finance has entered,
 * Paid is only ever what has been marked paid, and both read $0 for a company
 * with nothing recorded rather than being invented from somewhere. The card says
 * how many of the containers behind a figure actually carried one.
 */

const COMPLETE_STATUSES = new Set(['empty_returned', 'empty_returned_to_terminal', 'completed']);
const CANCELLED_STATUSES = new Set(['cancelled', 'canceled']);

// The money is Admin's and Finance's, the same two roles that own pricing
// everywhere else in this app (QUOTE_LOCATION_MANAGER_ROLES, and FINANCE_ONLY in
// the dashboard projection). Dispatch and the clerk get the operational half of
// the card. This is not decoration: the server does not send them the numbers, so
// drawing the row would draw zeros for a company that has been quoted $15,600.
const MONEY_ROLES = new Set(['admin', 'finance']);
// Who may add a Customer. Mirrors createBusinessCustomerRecord in
// functions/src/index.ts, which is Admin alone since 2026-08-21 ("只能admin加") -
// a button drawn for anyone else would be a button that answers "Only Admin can
// add Customers". Dispatcher could until that day and never did: every Customer
// and Driver in production arrived through the imports.
const ADD_CUSTOMER_ROLES = new Set(['admin']);
const INTERNAL_ROLES = new Set(['admin', 'dispatcher', 'clerk', 'finance']);

const CHARGE_FIELDS = Object.freeze([
  'pier_pass_cents', 'ctf_cents', 'demurrage_cents', 'exam_fee_cents',
  'service_fee_cents', 'per_diem_cents', 'other_charge_cents',
  'customer_special_fee_cents', 'exception_fee_cents',
]);

const DAY_MS = 86_400_000;
const DUE_SOON_MS = 2 * DAY_MS;

// A company is Delayed when the lateness is a pattern rather than an incident:
// five late boxes, or a tenth of everything open provided there is more than one.
// A single late container is always a Warning however small the account - without
// that floor a seven-container customer with one late box scored the same red as a
// customer with twenty, and a badge that is red for everyone tells you nothing.
const DELAYED_COUNT = 5;
const DELAYED_SHARE = 0.1;
const DELAYED_SHARE_FLOOR = 2;

const COPY = Object.freeze({
  en: Object.freeze({
    addCustomer: 'Add Customer',
    title: 'Company Overview',
    companies: 'Companies',
    containers: 'Containers',
    completed: 'Completed',
    completedUnpaid: 'Unpaid',
    open: 'Open',
    pastLfd: 'Past LFD',
    quoted: 'Quoted',
    revenue: 'Revenue',
    paid: 'Paid',
    outstanding: 'Outstanding',
    active: 'Active',
    warning: 'Warning',
    delayed: 'Delayed',
    settled: 'Settled',
    empty: 'No containers on file.',
    completionOf: 'empty returned',
    quotedOn: (quoted, total) => `Quoted on ${quoted} of ${total}`,
    noQuote: 'No quote on file',
    lateCount: (count) => `${count} past LFD`,
    dueSoonCount: (count) => `${count} due within 48h`,
    driverCount: (count) => `${count} with a driver`,
    ofFleet: 'of the fleet',
  }),
  zh: Object.freeze({
    addCustomer: '添加客户',
    title: '客户总览',
    companies: '客户',
    containers: '柜量',
    completed: '已完成',
    completedUnpaid: '已完成未收款',
    open: '进行中',
    pastLfd: '超期',
    quoted: '报价',
    revenue: '应收',
    paid: '已付',
    outstanding: '未付',
    active: '正常',
    warning: '注意',
    delayed: '延误',
    settled: '已结清',
    empty: '暂无柜子。',
    completionOf: '已还空',
    quotedOn: (quoted, total) => `${total} 柜中 ${quoted} 柜有报价`,
    noQuote: '暂无报价',
    lateCount: (count) => `${count} 柜超期`,
    dueSoonCount: (count) => `${count} 柜 48 小时内到期`,
    driverCount: (count) => `${count} 柜已派司机`,
    ofFleet: '占全部',
  }),
});

function copyFor(locale) {
  return String(locale || 'en').startsWith('zh') ? COPY.zh : COPY.en;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

// Whole dollars. Drayage is priced in round hundreds and there are up to a dozen
// figures on screen at once; ".00" on every one of them is a dozen pieces of
// punctuation carrying no information. The stored value stays in cents.
function wholeDollars(centsValue, locale) {
  const numeric = Number(centsValue);
  const rounded = Number.isFinite(numeric) ? Math.round(numeric / 100) * 100 : 0;
  return formatLocalizedCurrency(rounded, locale).replace(/\.00\b/u, '');
}

function millis(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() : null;
  if (typeof value?.toDate === 'function') return millis(value.toDate());
  if (Number.isFinite(value)) return Number(value);
  return null;
}

/**
 * Finished means the empty is back, and the field that records that is
 * ocean_status.
 *
 * Counted against production on 2026-08-11, after Alan asked why a table full of
 * 已还空 produced a card reading 0 已完成:
 *
 *   525 containers, every one of them status "pending" (479) or "past_due" (46)
 *   59  ocean_status == empty_returned      <- 已还空, what the table shows
 *   0   status == empty_returned            <- what this function used to read
 *   0   is_empty_returned, 0 canonical_completed
 *   3   ocean_status == cancelled, 0 status == cancelled
 *
 * `status` is the dispatch workflow's own field and nothing has ever driven it
 * past past_due, so every completion test above it was dead code answering
 * false. ocean_status is the one the clerks actually maintain. The three older
 * conditions stay: they are the canonical completion the workflow would set if
 * it ever ran to the end, and an OR cannot double-count.
 */
function isComplete(row) {
  return row?.canonical_completed === true
    || row?.is_empty_returned === true
    || COMPLETE_STATUSES.has(String(row?.status ?? '').toLowerCase())
    || COMPLETE_STATUSES.has(String(row?.ocean_status ?? '').toLowerCase());
}

function isCancelled(row) {
  return CANCELLED_STATUSES.has(String(row?.status ?? '').toLowerCase())
    || CANCELLED_STATUSES.has(String(row?.ocean_status ?? '').toLowerCase());
}

/**
 * Finished, and the money for it has not arrived.
 *
 * The one number Finance and the owner asked for (Alan, 2026-08-11: "每个公司都要
 * 有完成没有received的数量status给finance和老板"). Completion is operational and
 * payment is not, so a company can be at 100% delivered and still be the account
 * you have to chase - which is invisible on a card that only counts containers.
 *
 * customer_paid is the same flag the dashboard's Customer Paid column writes, and
 * absent means unpaid rather than unknown: 0 of the 525 live containers carry it,
 * so every completed container is currently outstanding, which is the true answer
 * rather than a flattering one.
 */
function isCompleteUnpaid(row) {
  return isComplete(row) && row?.customer_paid !== true;
}

/**
 * The agreed price for the job, in cents.
 *
 * customer_quote_display is what the workbook import and the quote snapshot both
 * write, and it is prose as often as it is a number - the five distinct values in
 * production on 2026-08-08 were "550", "$600", "$620", "600(货净重≤44000)" and
 * "500\n全包价". The first run of digits is the price in every one of them.
 * customer_quote_cents is preferred when Finance has set it, because that is a
 * number someone typed as a number.
 */
export function rowQuoteCents(row) {
  const stored = Number(row?.customer_quote_cents);
  if (Number.isFinite(stored) && stored > 0) return Math.round(stored);
  const display = row?.customer_quote_display;
  if (typeof display !== 'string') return 0;
  const match = display.match(/(\d[\d,]*(?:\.\d+)?)/u);
  if (!match) return 0;
  const amount = Number(match[1].replaceAll(',', ''));
  return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
}

function rowAccessorialCents(row) {
  return CHARGE_FIELDS.reduce((sum, field) => {
    const value = Number(row?.[field]);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);
}

function rowIsPastDue(row, now) {
  if (isComplete(row) || isCancelled(row)) return false;
  if (row?.is_past_due === true) return true;
  if (String(row?.status ?? '').toLowerCase() === 'past_due') return true;
  const lfd = millis(row?.lfd_at);
  return lfd !== null && lfd < now;
}

function rowIsDueSoon(row, now) {
  if (isComplete(row) || isCancelled(row) || rowIsPastDue(row, now)) return false;
  const lfd = millis(row?.lfd_at);
  return lfd !== null && lfd - now <= DUE_SOON_MS;
}

function badgeFor({ open, pastDue, dueSoon }) {
  if (open === 0) return 'settled';
  if (pastDue >= DELAYED_COUNT
    || (pastDue >= DELAYED_SHARE_FLOOR && pastDue >= open * DELAYED_SHARE)) return 'delayed';
  if (pastDue > 0 || dueSoon > 0) return 'warning';
  return 'active';
}

const UNNAMED = '—';

function emptyCompany(name) {
  return {
    name,
    unnamed: name === UNNAMED,
    rows: [],
    containers: 0,
    completed: 0,
    completedUnpaid: 0,
    open: 0,
    cancelled: 0,
    pastDue: 0,
    dueSoon: 0,
    withDriver: 0,
    quotedCount: 0,
    revenueCents: 0,
    paidCents: 0,
  };
}

/**
 * Groups the fleet by customer. Pure: the caller supplies `now` so the badge and
 * the past-LFD count are testable, and `role` decides whether the money is
 * computed at all.
 */
/**
 * @param customers Every customer on the books, so the ones with no containers
 *   still get a card (Alan, 2026-08-09: "不是所有的顾客。我说的是所有的顾客包括没有
 *   柜子的也放上去"). The page derived its companies from container rows alone, so a
 *   customer with nothing moving was invisible - which is exactly the customer you
 *   want to notice.
 */
export function companyOverviewModel({
  rows = [], customers = [], role = '', now = Date.now(),
} = {}) {
  const showMoney = MONEY_ROLES.has(String(role));
  const canAddCustomer = ADD_CUSTOMER_ROLES.has(String(role));
  const groups = new Map();

  // Seeded first so the zero-container companies exist before any row arrives,
  // and so a customer with containers is matched by name rather than duplicated.
  for (const customer of Array.isArray(customers) ? customers : []) {
    const name = String(
      typeof customer === 'string' ? customer : customer?.name ?? '',
    ).trim();
    if (name && !groups.has(name)) groups.set(name, emptyCompany(name));
  }

  for (const row of Array.isArray(rows) ? rows : []) {
    const name = String(row?.customer_name ?? '').trim() || UNNAMED;
    if (!groups.has(name)) groups.set(name, emptyCompany(name));
    const company = groups.get(name);
    const complete = isComplete(row);
    const cancelled = isCancelled(row);

    company.rows.push(row);
    company.containers += 1;
    if (complete) company.completed += 1;
    else if (cancelled) company.cancelled += 1;
    else company.open += 1;
    if (isCompleteUnpaid(row)) company.completedUnpaid += 1;
    if (rowIsPastDue(row, now)) company.pastDue += 1;
    else if (rowIsDueSoon(row, now)) company.dueSoon += 1;
    if (String(row?.pickup_driver_name ?? '').trim()) company.withDriver += 1;

    if (!showMoney) continue;
    const quote = rowQuoteCents(row);
    const value = quote + rowAccessorialCents(row);
    if (quote > 0) company.quotedCount += 1;
    company.revenueCents += value;
    if (row?.customer_paid === true) company.paidCents += value;
  }

  const companies = [...groups.values()]
    .map(({ rows: companyRows, ...company }) => ({
      ...company,
      // How many containers this company has in each Ocean Status, in the
      // reading order the table is sorted by (Alan, 2026-08-08). "278 containers"
      // says how big the account is; this says what is actually happening in it.
      statuses: oceanStatusCounts(companyRows),
      completionPercent: company.containers
        ? Math.round((company.completed / company.containers) * 100) : 0,
      outstandingCents: company.revenueCents - company.paidCents,
      badge: badgeFor(company),
    }))
    // Biggest book of business first. A container that names no customer is a
    // gap in the data, not a company, so it goes last however many rows it holds
    // - and it is shown rather than dropped, because the fleet has to add up.
    .sort((left, right) => Number(left.unnamed) - Number(right.unnamed)
      || right.containers - left.containers
      || left.name.localeCompare(right.name, 'en', { sensitivity: 'base' }));

  const sum = (key) => companies.reduce((total, company) => total + company[key], 0);
  const containers = sum('containers');
  const completed = sum('completed');

  return Object.freeze({
    showMoney,
    canAddCustomer,
    totals: Object.freeze({
      companies: companies.length,
      containers,
      completed,
      completedUnpaid: sum('completedUnpaid'),
      open: sum('open'),
      pastDue: sum('pastDue'),
      quotedCount: sum('quotedCount'),
      revenueCents: sum('revenueCents'),
      paidCents: sum('paidCents'),
      outstandingCents: sum('revenueCents') - sum('paidCents'),
      completionPercent: containers ? Math.round((completed / containers) * 100) : 0,
    }),
    companies: Object.freeze(companies.map((company) => Object.freeze(company))),
  });
}

export function companyOverviewVisibleToRole(role) {
  return INTERNAL_ROLES.has(String(role));
}

const RING_RADIUS = 19;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/**
 * The completion ring. At 0% the value arc is left out entirely: a zero-length
 * dash still paints its round line-cap, which drew a stray dot at twelve o'clock
 * on every card while nothing was complete.
 */
function ringMarkup(percent, tone) {
  const value = Math.max(0, Math.min(100, Number(percent) || 0));
  const dash = (value / 100) * RING_CIRCUMFERENCE;
  const arc = value > 0
    ? `<circle class="company-ring__value company-ring__value--${escapeHtml(tone)}" cx="22" cy="22" r="${RING_RADIUS}"
        stroke-dasharray="${dash.toFixed(2)} ${(RING_CIRCUMFERENCE - dash).toFixed(2)}"></circle>`
    : '';
  return `<svg class="company-ring" viewBox="0 0 44 44" role="img" aria-label="${value}%">
      <circle class="company-ring__track" cx="22" cy="22" r="${RING_RADIUS}"></circle>
      ${arc}
      <text class="company-ring__label" x="22" y="22" text-anchor="middle" dominant-baseline="central">${value}%</text>
    </svg>`;
}

function statMarkup(value, label, tone = '') {
  return `<div class="company-stat${tone ? ` company-stat--${escapeHtml(tone)}` : ''}">
      <strong>${escapeHtml(String(value))}</strong>
      <span>${escapeHtml(label)}</span>
    </div>`;
}

function kpiMarkup({ icon, value, label, note = '', tone = '' }) {
  return `<article class="company-kpi${tone ? ` company-kpi--${escapeHtml(tone)}` : ''}">
      <span class="company-kpi__icon" aria-hidden="true"><i class="ph ${escapeHtml(icon)}"></i></span>
      <div class="company-kpi__body">
        <span class="company-kpi__label">${escapeHtml(label)}</span>
        <strong class="company-kpi__value">${escapeHtml(String(value))}</strong>
        ${note ? `<span class="company-kpi__note">${escapeHtml(note)}</span>` : ''}
      </div>
    </article>`;
}

/**
 * One chip per Ocean Status this company actually holds, in the table's reading
 * order. Statuses it has none of are left out rather than shown as zero: a card
 * with six zeroes beside two real numbers buries the two.
 */
function statusCountsMarkup(statuses, locale) {
  const entries = Array.isArray(statuses) ? statuses : [];
  if (!entries.length) return '';
  return `<ul class="company-statuses">${entries.map(({ token, count }) => `
      <li class="company-status" data-status="${escapeHtml(token)}">
        <span>${escapeHtml(token === 'unknown' ? '—' : containerStatusLabel(token, locale))}</span>
        <strong>${count}</strong>
      </li>`).join('')}</ul>`;
}

function footnote(company, copy) {
  return [
    company.pastDue ? copy.lateCount(company.pastDue) : '',
    company.dueSoon ? copy.dueSoonCount(company.dueSoon) : '',
    company.withDriver ? copy.driverCount(company.withDriver) : '',
  ].filter(Boolean).join(' · ');
}

/**
 * Three figures and the count behind them. A company with nothing recorded gets
 * the caption alone rather than a row of three zeros, which reads as a fact and
 * is only an absence.
 */
function moneyRowMarkup(company, copy, locale) {
  if (!company.revenueCents && !company.paidCents) {
    return `<p class="company-quoted">${escapeHtml(copy.noQuote)}</p>`;
  }
  const money = (value) => wholeDollars(value, locale);
  return `<div class="company-money">
      <div><span>${escapeHtml(copy.revenue)}</span><strong>${escapeHtml(money(company.revenueCents))}</strong></div>
      <div><span>${escapeHtml(copy.paid)}</span><strong>${escapeHtml(money(company.paidCents))}</strong></div>
      <div><span>${escapeHtml(copy.outstanding)}</span><strong>${escapeHtml(money(company.outstandingCents))}</strong></div>
    </div>
    <p class="company-quoted">${escapeHtml(copy.quotedOn(company.quotedCount, company.containers))}</p>`;
}

function companyCardMarkup(company, { copy, locale, showMoney }) {
  const note = footnote(company, copy);
  return `<article class="company-card" data-company-badge="${escapeHtml(company.badge)}">
      <header class="company-card__head">
        <h2>${escapeHtml(company.name)}</h2>
        <span class="company-badge company-badge--${escapeHtml(company.badge)}">${escapeHtml(copy[company.badge])}</span>
      </header>
      <div class="company-card__stats">
        ${statMarkup(company.containers, copy.containers)}
        ${statMarkup(company.completed, copy.completed, company.completed ? 'done' : '')}
        ${showMoney ? statMarkup(company.completedUnpaid, copy.completedUnpaid, company.completedUnpaid ? 'owing' : '') : ''}
        ${statMarkup(company.open, copy.open)}
        ${ringMarkup(company.completionPercent, company.badge)}
      </div>
      <div class="company-progress" role="presentation">
        <span class="company-progress__value company-progress__value--${escapeHtml(company.badge)}"
          style="inline-size: ${company.completionPercent}%"></span>
      </div>
      ${statusCountsMarkup(company.statuses, locale)}
      ${showMoney ? moneyRowMarkup(company, copy, locale) : ''}
      ${note ? `<p class="company-note">${escapeHtml(note)}</p>` : ''}
    </article>`;
}

/**
 * The five figures, for the chrome bar (Alan, 2026-08-09: "把那个 kpi 5 个放到
 * boarder 里面"). They sat in a strip above the cards, which pushed the first
 * company most of a screen down the page.
 */
export function companyOverviewBarMarkup({ model, locale = 'en' } = {}) {
  // The door to Add Customer, and for a while there was none.
  //
  // The plus in the top bar adds a Task and only a Task (Alan, 2026-08-09: "这个
  // 加号只能加任务"), and the note left behind said Customers and Drivers are
  // created from their own pages. No page ever grew the link: nothing in the app
  // routed to #/create/customer at all, so the form, the route and the callable
  // all existed and could not be reached (Alan, 2026-08-21: "我在adminpage 根本
  // 没看见加顾客的选项"). This is that page - the Companies list is where
  // customers are read - and this is the link.
  const copy = copyFor(locale);
  const canAdd = (model ?? companyOverviewModel({})).canAddCustomer === true;
  const add = canAdd
    ? `<a class="company-add-customer" href="#/create/customer" data-add-customer>${escapeHtml(copy.addCustomer)}</a>`
    : '';
  return `<div class="company-kpis">${companyOverviewKpis({ model, locale }).map(kpiMarkup).join('')}</div>${add}`;
}

function companyOverviewKpis({ model, locale = 'en' } = {}) {
  const copy = copyFor(locale);
  const { totals, showMoney } = model ?? companyOverviewModel({});
  const money = (value) => wholeDollars(value, locale);
  return [
    { icon: 'ph-buildings', value: totals.companies, label: copy.companies },
    { icon: 'ph-shipping-container', value: totals.containers, label: copy.containers },
    {
      icon: 'ph-check-circle',
      value: totals.completed,
      label: copy.completed,
      note: `${totals.completionPercent}% ${copy.completionOf}`,
      tone: 'good',
    },
    { icon: 'ph-clock-countdown', value: totals.open, label: copy.open },
    {
      icon: 'ph-warning',
      value: totals.pastDue,
      label: copy.pastLfd,
      tone: totals.pastDue ? 'late' : '',
    },
    ...(showMoney ? [{
      icon: 'ph-currency-circle-dollar',
      value: money(totals.revenueCents),
      label: copy.revenue,
      note: copy.quotedOn(totals.quotedCount, totals.containers),
    }] : []),
  ];
}

export function companyOverviewMarkup({ model, locale = 'en' } = {}) {
  const copy = copyFor(locale);
  const { totals, companies, showMoney } = model ?? companyOverviewModel({});
  const money = (value) => wholeDollars(value, locale);
  const kpis = [
    { icon: 'ph-buildings', value: totals.companies, label: copy.companies },
    { icon: 'ph-shipping-container', value: totals.containers, label: copy.containers },
    {
      icon: 'ph-check-circle',
      value: totals.completed,
      label: copy.completed,
      note: `${totals.completionPercent}% ${copy.completionOf}`,
      tone: 'good',
    },
    { icon: 'ph-clock-countdown', value: totals.open, label: copy.open },
    {
      icon: 'ph-warning',
      value: totals.pastDue,
      label: copy.pastLfd,
      tone: totals.pastDue ? 'late' : '',
    },
    ...(showMoney ? [{
      icon: 'ph-currency-circle-dollar',
      value: money(totals.revenueCents),
      label: copy.revenue,
      note: copy.quotedOn(totals.quotedCount, totals.containers),
    }] : []),
  ];

  // No page title (Alan, 2026-08-08). The shell already runs a photographic band
  // across the top of every authenticated page, and a heading sitting on it read
  // as a caption on the photograph rather than as the name of the page. The KPI
  // bar says what this is without being told.
  return `<section class="company-overview" aria-label="${escapeHtml(copy.title)}">
      ${companies.length
    ? `<div class="company-grid">${companies
      .map((company) => companyCardMarkup(company, { copy, locale, showMoney })).join('')}</div>`
    : `<p class="company-empty">${escapeHtml(copy.empty)}</p>`}
    </section>`;
}
